/**
 * softBudgetDashboard — Compact agent-smoke snapshot of live soft budgets (SPY-301).
 * Advisory only; never gates pnpm check.
 */

import { ARAM_SOFT_PERF_BUDGET } from '../../domain/perf/aramSoftPerfBudget.js';

/** Soft residual warn when phaseOverPct exceeds this floor (FID-PERF-09). */
export const PHASE_TIGHTEN_WARN_PCT = 5;

/**
 * Live evaluateSoft metrics are per-phase { p95Ms, softCapMs }.
 * Residual sample (FID-PERF-01) derives worstPhase when not pre-set.
 */
function resolveWorstPhase(metrics) {
  if (!metrics || typeof metrics !== 'object') {
    return { worstPhase: null, worstMs: null };
  }
  if (typeof metrics.worstPhase === 'string' && Number.isFinite(metrics.worstMs)) {
    return { worstPhase: metrics.worstPhase, worstMs: metrics.worstMs };
  }
  let worstPhase = null;
  let worstMs = null;
  let bestOver = -Infinity;
  for (const [id, row] of Object.entries(metrics)) {
    if (!row || typeof row !== 'object') continue;
    const p95 = Number(row.p95Ms);
    if (!(p95 > 0)) continue;
    const cap = Number(row.softCapMs);
    const over = Number.isFinite(cap) ? p95 - cap : p95;
    if (over > bestOver || (over === bestOver && p95 > worstMs)) {
      bestOver = over;
      worstPhase = id;
      worstMs = p95;
    }
  }
  return { worstPhase, worstMs };
}

function softCapForPhase(phaseId) {
  const fromBudget = ARAM_SOFT_PERF_BUDGET.phases.softCapMs[phaseId];
  return Number.isFinite(fromBudget) ? fromBudget : null;
}

function computePhaseOverMs(worstPhase, worstMs) {
  if (!worstPhase || !Number.isFinite(worstMs)) return null;
  const cap = softCapForPhase(worstPhase);
  if (!Number.isFinite(cap)) return null;
  return Math.max(0, Math.round((worstMs - cap) * 100) / 100);
}

function computePhaseOverPct(worstPhase, phaseOverMs) {
  if (!Number.isFinite(phaseOverMs) || phaseOverMs <= 0) return 0;
  const cap = softCapForPhase(worstPhase);
  if (!Number.isFinite(cap) || cap <= 0) return 0;
  return Math.round((phaseOverMs / cap) * 1000) / 10;
}

export function buildSoftBudgetDashboard({
  evaluation,
  phaseEvaluation,
  allocEvaluation,
  recording = false
} = {}) {
  const warnings = [
    ...(evaluation?.warnings || []),
    ...(phaseEvaluation?.warnings || []),
    ...(allocEvaluation?.warnings || [])
  ];
  const deltaMb = allocEvaluation?.metrics?.deltaMb;
  const warnDeltaMb = ARAM_SOFT_PERF_BUDGET.alloc.warnDeltaMb;
  const allocDeltaMb = Number.isFinite(deltaMb) ? deltaMb : null;
  const { worstPhase, worstMs } = resolveWorstPhase(phaseEvaluation?.metrics);
  const worstPhaseMs = Number.isFinite(worstMs) ? worstMs : null;
  const phaseOverMs = computePhaseOverMs(worstPhase, worstPhaseMs);
  const phaseOverPct = computePhaseOverPct(worstPhase, phaseOverMs);
  return {
    hook: '__debugSoftBudgetDashboard',
    advisory: true,
    recording: !!recording,
    fpsOk: evaluation?.ok !== false,
    phaseOk: phaseEvaluation?.ok !== false,
    allocOk: allocEvaluation?.ok !== false,
    ok: warnings.length === 0,
    warnings,
    warnDeltaMb,
    allocDeltaMb,
    warnDelta: Number.isFinite(allocDeltaMb) && allocDeltaMb > warnDeltaMb,
    phaseWarn: phaseEvaluation?.ok === false,
    worstPhase,
    worstPhaseMs,
    phaseOverMs,
    phaseOverPct,
    phaseResidualWarn: Number.isFinite(phaseOverMs) && phaseOverMs > 0,
    phaseTightenFloor: PHASE_TIGHTEN_WARN_PCT,
    phaseTightenWarn: phaseOverPct > PHASE_TIGHTEN_WARN_PCT
  };
}

/**
 * One-line CI / soak log snippet (SPY-316). Advisory; never gates check.
 * @param {ReturnType<typeof buildSoftBudgetDashboard>} snap
 */
export function formatSoftBudgetDashboardCi(snap, opts = {}) {
  const row = snap || buildSoftBudgetDashboard();
  const flag = row.ok ? 'SOFT_OK' : 'SOFT_WARN';
  const n = Array.isArray(row.warnings) ? row.warnings.length : 0;
  const allocDelta = row.allocDeltaMb === null || row.allocDeltaMb === undefined
    ? 'null'
    : String(row.allocDeltaMb);
  const soakTag = opts.soak ? ' soak=true' : '';
  return (
    `[soft-budget] ${flag} fps=${row.fpsOk} phase=${row.phaseOk} alloc=${row.allocOk}` +
    ` warnings=${n} warnDelta=${!!row.warnDelta} allocDeltaMb=${allocDelta}` +
    ` phaseWarn=${!!row.phaseWarn} worstPhase=${row.worstPhase ?? 'null'}` +
    ` worstPhaseMs=${row.worstPhaseMs ?? 'null'} phaseOverMs=${row.phaseOverMs ?? 'null'}` +
    ` phaseOverPct=${row.phaseOverPct ?? 'null'} phaseTightenWarn=${!!row.phaseTightenWarn}` +
    ` phaseTightenFloor=${PHASE_TIGHTEN_WARN_PCT}${soakTag}`
  );
}
